import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import _get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { TaskFilterInputObject } from 'graphql-common';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import { Option, Values } from '@lib/interfaces/form';
import MonitoringFilterFieldNames from '@lib/enums/fieldNames/monitoringFilterFieldNames';
import useSitesPerformanceMetricsByDate from 'utils/fetch/hooks/useSitesPerformanceMetricsByDate';
import CircleLoaderWrapper from '@lib/components/CircleLoaderWrapper/CircleLoaderWrapper';
import useSectorsPerformanceMetricsByDate from 'utils/fetch/hooks/useSectorsPerformanceMetricsByDate';
import EmptyState from '@lib/components/EmptyState/EmptyState';
import { IconName } from '@lib/components/Modal/enums';
import getAnomalyKind from '../utils/getStatusKind';
import getFilterFieldNameByMonitoringEntity from '../utils/getFilterFieldNameByMonitoringEntity';
import { MonitoringEntity } from '../enums';
import prepareAnomaliesChart from '../utils/prepareAnomaliesChart';
import { AnomaliesChartData } from '../types';
import getMonitoringEntity from '../utils/getMonitoringEntity';
import getStatusOption from '../utils/getStatusOption';
import styles from './MonitoringRangesGraph.module.scss';

const GRAPH_HEIGHT = '350px';

export default function MonitoringRangesGraph({
  filterParams,
  children,
  taskFilters,
}: {
  filterParams: Values;
  taskFilters: TaskFilterInputObject[];
  children: React.ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const locale = _get(i18n, 'language');

  const monitoringEntity = getMonitoringEntity({ filterParams });
  const statusKind = getAnomalyKind({ filterParams });
  const statusOption = getStatusOption({ t, status: statusKind });
  const yLabel = statusOption?.label || t('anomalies');

  const sites = _get(
    filterParams,
    MonitoringFilterFieldNames.Sites,
    [],
  ) as Option[];
  const sectors = _get(
    filterParams,
    MonitoringFilterFieldNames.Sectors,
    [],
  ) as Option[];
  const filterBySites = sites.map((item) => item.value) as string[];
  const filterBySectors = sectors.map((item) => item.value) as string[];

  const {
    data: sitesData,
    firstLoading: sitesFirstLoading,
    error: sitesError,
    loading: sitesLoading,
  } = useSitesPerformanceMetricsByDate({
    ids: filterBySites,
    taskFilters,
    statusKind,
    skip: monitoringEntity !== MonitoringEntity.Site || !filterBySites?.length,
  });

  const {
    data: sectorsData,
    firstLoading: sectorsFirstLoading,
    error: sectorsError,
    loading: sectorsLoading,
  } = useSectorsPerformanceMetricsByDate({
    ids: filterBySectors,
    taskFilters,
    statusKind,
    skip:
      monitoringEntity !== MonitoringEntity.Sector || !filterBySectors?.length,
  });

  const hasError = !!(sitesError || sectorsError);

  const period = _get(
    filterParams,
    getFilterFieldNameByMonitoringEntity(
      monitoringEntity,
      MonitoringFilterFieldNames.Period,
    ),
    [],
  );

  const chartData: AnomaliesChartData = useMemo(() => {
    const dateRange = {
      min: period?.[0],
      max: period?.[1],
    };
    if (monitoringEntity === MonitoringEntity.Site) {
      return prepareAnomaliesChart({
        input: sitesData || [],
        optionArray: sites,
        dateRange,
        locale,
        yLabel,
      });
    }
    if (monitoringEntity === MonitoringEntity.Sector) {
      return prepareAnomaliesChart({
        input: sectorsData || [],
        optionArray: sectors,
        dateRange,
        locale,
        yLabel,
      });
    }
    return prepareAnomaliesChart({
      input: [],
      optionArray: [],
      dateRange,
      locale,
      yLabel,
    });
  }, [
    yLabel,
    monitoringEntity,
    period,
    sectors,
    sectorsData,
    sites,
    sitesData,
    locale,
  ]);

  let title = '';
  if (monitoringEntity === MonitoringEntity.Site) {
    title = t('monitoring-sites-ranges');
  } else if (monitoringEntity === MonitoringEntity.Sector) {
    title = t('monitoring-sectors-ranges');
  }

  return (
    <div>
      <DashboardTitleHeader
        bottomSpace="middle"
        title={title}
        titleVariant="h3"
      />
      {hasError ? (
        <EmptyState
          icon={IconName.Cancel}
          text={t('refresh-or-retry-later')}
          title={t('failed-to-load')}
        />
      ) : (
        <>
          {children}
          <CircleLoaderWrapper isLoading={sitesLoading || sectorsLoading}>
            <WhiteBox verticalPaddings="small" horizontalPaddings="small">
              <div
                className={classNames(styles.graphRoot, {
                  [styles.graphRootHidden]:
                    sitesFirstLoading || sectorsFirstLoading,
                })}
              >
                <Chart
                  key={`${monitoringEntity}-${sites.length}-${sectors.length}-${period?.[0]}-${period?.[1]}`}
                  options={chartData.options}
                  series={chartData.series}
                  type="line"
                  width="100%"
                  height={GRAPH_HEIGHT}
                />
              </div>
            </WhiteBox>
          </CircleLoaderWrapper>
        </>
      )}
    </div>
  );
}
